import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initWebVitals } from "../lib/web-vitals";
import { initGA4 } from "../lib/ga4";
import { initDeferredScripts } from "../lib/deferred-scripts";
import { Layout } from "@/components/site/Layout";
import { DynamicBreadcrumbJsonLd } from "@/components/seo/DynamicBreadcrumbJsonLd";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#1a1410" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "format-detection", content: "telephone=no" },
      { name: "HandheldFriendly", content: "true" },
      { name: "google-site-verification", content: "BLqQMvWkGT9Ll_XEErIeIZet_cQ9UGS5k-zHdLYVIpQ", key: "google-site-verification" },
      { name: "author", content: "Raj Shamani" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1", key: "robots" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "og:type", content: "website", key: "og:type" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      // Title, description and og/twitter title+description+image are defined
      // per-route so leaf pages own their snippet and social preview.
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/b5998d3c-e04e-435a-9839-68a1cdb628ea/space-grotesk-latin.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/9ede9130-d5ae-4284-afe8-1a81635eddd2/dm-sans-latin.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
    ],
    scripts: [
      {
        async: true,
        src: "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7709339126499963",
        crossOrigin: "anonymous",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://rajshamani.life/#website",
              "name": "Raj Shamani",
              "alternateName": "Raj Shamani Official Website",
              "url": "https://rajshamani.life/",
              "inLanguage": "en",
              "description": "Entrepreneur, investor, and host of Figuring Out — one of India's most-watched business podcasts. Building brands, backing founders, sharing what works.",
              "publisher": { "@id": "https://rajshamani.life/#organization" },
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://rajshamani.life/?q={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "Organization",
              "@id": "https://rajshamani.life/#organization",
              "name": "Raj Shamani",
              "url": "https://rajshamani.life/",
              "logo": {
                "@type": "ImageObject",
                "@id": "https://rajshamani.life/#logo",
                "url": "https://rajshamani.life/__l5e/assets-v1/4d702d47-9f07-4958-aa4d-cec3235d2687/og-default.jpg",
                "width": 1200,
                "height": 630,
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Business Inquiries",
                "email": "partnerships@figuringout.co",
                "url": "https://rajshamani.life/contact",
              },
              "sameAs": [
                "https://www.youtube.com/@rajshamani",
                "https://www.instagram.com/rajshamani",
                "https://www.linkedin.com/in/rajshamani",
                "https://twitter.com/rajshamani",
              ],
            },
          ],
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    initGA4();
    initWebVitals();
    initDeferredScripts();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Layout>
        <DynamicBreadcrumbJsonLd />
        <Outlet />
      </Layout>
    </QueryClientProvider>
  );
}
